import { useState } from 'react';
import { useParams, useLoaderData, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Intro from '../../components/common/Intro/Intro';
import SummarySection from '../../components/common/SummarySection/SummarySection';
import Section from '../../components/common/Section/Section';
import { capitalizeFirstLetter } from '../../utils/texts';
import { Colors } from '../../Constants';
import Card from '../../components/common/Card/Card';
import './MemberDetail.css';
import { DropdownOptionType, MemberDetailResponseType, SummarySectionListItemType } from '../../types';
import MemberDetailVotingSummaryChartSection from '../../components/memberDetailCharts/MemberDetailVotingSummaryChartSection';
import MemberDetailAttendanceSummaryChartSection from '../../components/memberDetailCharts/MemberDetailAttendanceSummaryChartSection';
import { Dropdown } from 'primereact/dropdown';

function MemberDetail() {
    const [selectedAttendance, setSelectedAttendance] = useState<string | number | null>(null);
    const { t } = useTranslation();
    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();
    const showAllOption = {
        name: t('total-sum'),
        code: 0
    };
    const [selectedParty, setSelectedParty] = useState<DropdownOptionType | null>(showAllOption);

    const loaderData = useLoaderData() as { data: MemberDetailResponseType };
    const memberData = loaderData.data;
    const memberWasActiveAtLeastOnce = memberData?.hlasovani?.aktivni_platna > 0;

    const member = t('member', { context: memberData.pohlavi });

    // TODO: Should be done on BE?
    let total = memberData.hlasovani.aktivni_platna;
    if (memberData.hlasovani.sumarizace.hasOwnProperty('nepřít.')) {
        total += memberData.hlasovani.sumarizace['nepřít.'];
    }
    if (memberData.hlasovani.sumarizace.hasOwnProperty('omluven')) {
        total += memberData.hlasovani.sumarizace['omluven'];
    }

    const formatMembershipDuration = (dateFrom: string, dateTo: string | null) => {
        if (dateFrom && dateTo) return `${dateFrom} - ${dateTo}`;
        else if (dateFrom) return `${t('interval-from')} ${dateFrom}`;
        else if (dateTo) return `${t('interval-to')} ${dateTo}`;
        else return '';
    };

    const partyIsSelected = selectedParty?.code !== 0;
    let selectedPartyData = memberData?.votes_per_party.find((item) => item.party_id === selectedParty?.code);
    const selectedPartyMeetingAttendance = selectedPartyData
        ? Object.values(selectedPartyData.attendance_sum).reduce((sum, val) => sum + val, 0)
        : 0;
    const selectedPartyMeetingAttendanceString = `(${selectedPartyMeetingAttendance} ${t('meetings')} ${t('for')} ${
        selectedParty?.name
    })`;

    const memberIsActiveWholeTerm = memberData.dochazka.mandat === memberData.dochazka.celkem_zasedani;
    const votingPercentageAttendanceString = `${
        partyIsSelected ? selectedPartyData?.attendance_stat.toFixed(2) : memberData.dochazka.attendance_stat.toFixed(2)
    }%`;
    const attendedCountString = `(${
        partyIsSelected ? selectedPartyData?.votes_present : memberData.hlasovani.aktivni_platna
    } ${t('of')} ${partyIsSelected ? selectedPartyData?.votes_total : total})`;

    const summarySectionItems = [
        {
            icon: 'description',
            text: memberIsActiveWholeTerm
                ? `${capitalizeFirstLetter(t('mandate'))} ${t('whole')} ${t('term')}`
                : `${capitalizeFirstLetter(t('mandate'))} ${memberData.dochazka.mandat} ${t('of')} ${
                      memberData.dochazka.celkem_zasedani
                  } ${t('meetings')}`,

            highlightSubString: memberIsActiveWholeTerm
                ? t('whole')
                : `${memberData.dochazka.mandat} ${t('of')} ${memberData.dochazka.celkem_zasedani}`
        },
        ...(partyIsSelected
            ? [
                  {
                      icon: '',
                      text: selectedPartyMeetingAttendanceString,
                      mutedSubString: selectedPartyMeetingAttendanceString,
                      className: 'additional-mandate-info'
                  }
              ]
            : []),
        {
            icon: 'check_box',
            text: `${votingPercentageAttendanceString} ${t('voting-attendance')} ${attendedCountString}`,
            highlightSubString: votingPercentageAttendanceString,
            mutedSubString: attendedCountString
        }
    ] as SummarySectionListItemType[];

    if (memberWasActiveAtLeastOnce) {
        summarySectionItems.push({
            icon: '',
            text: t('councilMember_comparison'),
            highlightSubString: t('councilMember_comparison'),
            type: 'link' as 'link',
            linkUrl: `../../analyzy/porovnani_zastupitelu/?prvni_clen=${memberData.id}`
        });
    }

    const memberHasMoreThanOneParty = memberData?.politicke_strany?.length > 1;
    const memberIsActive = memberData?.aktivni_clen === 1;

    const partySelectionDropdown = (
        <div className='dropdown'>
            <h6>{t('displayed-data', { ns: 'translation' })}</h6>
            <Dropdown
                inputId='compareSessionSelection'
                value={selectedParty}
                options={[
                    showAllOption,
                    ...(memberData?.politicke_strany
                        ?.filter((party, index, arr) => arr.findIndex((p) => p.id === party.id) === index)
                        .map((party) => ({
                            name: party.zkratka,
                            code: party.id
                        })) || [])
                ]}
                onChange={(e) => {
                    setSelectedAttendance(null);
                    setSelectedParty(e.value);
                }}
                optionLabel='name'
            />
        </div>
    );

    let attendanceMapSource;
    if (selectedAttendance !== null) {
        if (!partyIsSelected) {
            attendanceMapSource = memberData?.zasedani?.[selectedAttendance];
        } else {
            attendanceMapSource = selectedPartyData?.[selectedAttendance as keyof typeof selectedPartyData];
        }
    }

    return (
        <>
            <Intro
                title={memberData?.jmeno}
                titleColor={memberIsActive ? memberData.politicke_strany[0].strana_barva : Colors.darkGray}
                subtitle={
                    memberIsActive
                        ? `${capitalizeFirstLetter(t('active'))} ${member}`
                        : `${capitalizeFirstLetter(t('inactive'))} ${member}`
                }
                titleColorSubtitle={memberData?.politicke_strany[0].zkratka}
            />
            <SummarySection
                summaryItems={summarySectionItems}
                sectionProps={{
                    titleElement: partySelectionDropdown,
                    titleElementCondition: memberHasMoreThanOneParty
                }}
            />
            {memberWasActiveAtLeastOnce && (
                <>
                    {(memberHasMoreThanOneParty || !memberIsActive) && (
                        <>
                            <Section title={t('affiliation-history')} contentClassName='affiliation-history-link-list'>
                                {memberData.politicke_strany.map((party, i) => {
                                    const membershipDuration = formatMembershipDuration(party.datum_od, party.datum_do);
                                    const activeParty = i === 0 && memberIsActive;

                                    return (
                                        <Link to={`../../subjekty/${party.id}`} key={party.id}>
                                            <span
                                                style={activeParty ? { color: party.strana_barva } : {}}
                                                className={activeParty ? 'active-party-name' : 'inactive-muted-text'}
                                            >
                                                {party.zkratka}
                                            </span>
                                            {activeParty ? (
                                                <span> ({t('now')})</span>
                                            ) : (
                                                <span className='inactive-muted-text'> ({membershipDuration})</span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </Section>
                        </>
                    )}
                    <MemberDetailVotingSummaryChartSection memberData={memberData} selectedParty={selectedParty} />
                    <MemberDetailAttendanceSummaryChartSection
                        selectedAttendanceState={{ value: selectedAttendance, setValue: setSelectedAttendance }}
                        memberData={memberData}
                        selectedParty={selectedParty}
                    />
                </>
            )}
            {selectedAttendance !== null && (
                <Section
                    title={`${t('member-attendance')} - ${
                        !partyIsSelected
                            ? selectedAttendance
                            : Object.keys(memberData?.zasedani)[selectedAttendance as number]
                    }`}
                    contentClassName='meetings-link-list'
                >
                    {Array.isArray(attendanceMapSource) &&
                        attendanceMapSource?.map((item: { id: number; meeting_number: string }) => (
                            <Card
                                key={item.id}
                                link={`/${municipalitaId}/${bodyType}/${zastupitelstvoId}/zasedani/${item.id}`}
                            >
                                <span>
                                    {t('meeting-no')} {item.meeting_number}
                                </span>
                                <span className='material-symbols-rounded'>chevron_right</span>
                            </Card>
                        ))}
                </Section>
            )}
        </>
    );
}

export default MemberDetail;
